/**
 * Represents the ability to inject the underline and dot roots.
 *
 * Different website or types of editors might require different places
 * for these roots to go.
 *
 * For example, Outlook has a fixed bottom toolbar, so the dot root dom should be
 * injected into that fixed bottom toolbar. The email message editor is in a different
 * part of the dom, so the underline layer should be injected near that editor.
 */
export interface Injection {
  /**
   * Injects a div into the correct place for the underline root.
   *
   * This dom is meant to be the target of a shadow portal.
   *
   * @see ShadowPortal
   *
   * @param onCreate a callback called right before adding the div element to the spot.
   * Use this to add styles or modify the div before setting it up.
   */
  injectUnderlineRoot(onCreate?: (div: HTMLDivElement) => void): HTMLElement;

  /**
   * Injects a div into the correct place for the dot root.
   *
   * This dom is meant to be the target of a shadow portal.
   *
   * @see ShadowPortal
   *
   * @param onCreate a callback called right before adding the div element to the spot.
   * Use this to add styles or modify the div before setting it up.
   */
  injectDotRoot(onCreate?: (div: HTMLDivElement) => void): HTMLElement;
}

export const UNDERLINE_ROOT_CLASS = "__ce-underline-root";
export const DOT_ROOT_CLASS = "__ce-dot-root";

export function createRootDiv(
  document: HTMLDocument,
  className: string,
  onCreate?: (div: HTMLDivElement) => void
): HTMLDivElement {
  const root = document.createElement("div");
  root.classList.add(className);
  onCreate?.(root);
  return root;
}

export function inject(
  document: HTMLDocument,
  className: string,
  querySelector: string | (() => Element),
  onCreate?: (div: HTMLDivElement) => void,
  append?: boolean
): HTMLDivElement {
  const root = createRootDiv(document, className, onCreate);
  const injectionPoint =
    typeof querySelector === "string"
      ? document.querySelector(querySelector)
      : querySelector();
  if (append) {
    injectionPoint?.append(root);
    return root;
  }
  injectionPoint?.prepend(root);
  return root;
}
